import {
  BaseElementWidget,
  CustomWidget,
  http,
  MessageHub,
  SPI,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import DutyScheduleCalendarVue from './DutyScheduleCalendar.vue';

interface EmployeeItem {
  id: number;
  name: string;
  code: string;
}

interface OffRecord {
  id?: number;
  dutyDate?: string;
  employeeId?: number;
  employeeName?: string;
  employeeCode?: string;
  startTime?: string;
  endTime?: string;
  updateByName?: string;
  updateTime?: string;
}

interface ChangeLogItem {
  id?: number;
  startTime?: string;
  endTime?: string;
  updateByName?: string;
  updateTime?: string;
}

const MODULE_NAME = 'deliAriesCoreSeller';

@SPI.ClassFactory(
  BaseElementWidget.Token({
    viewType: ViewType.Table,
    widget: 'DutyScheduleCalendar'
  })
)
export class DutyScheduleCalendarWidget extends CustomWidget {
  // ========== Year calendar state ==========
  @Widget.Reactive()
  protected currentYear: number = new Date().getFullYear();

  @Widget.Reactive()
  protected expandedMonth: number = 0;

  @Widget.Reactive()
  protected showYearPicker: boolean = false;

  @Widget.Reactive()
  protected yearPickerStart: number = new Date().getFullYear() - (new Date().getFullYear() % 12);

  @Widget.Reactive()
  protected weekDays: string[] = ['一', '二', '三', '四', '五', '六', '日'];

  // ========== Duty time config ==========
  @Widget.Reactive()
  protected dutyStartTime: string = '';

  @Widget.Reactive()
  protected dutyEndTime: string = '';

  @Widget.Reactive()
  protected showChangeLog: boolean = false;

  @Widget.Reactive()
  protected changeLogs: ChangeLogItem[] = [];

  // ========== Selected date ==========
  @Widget.Reactive()
  protected selectedDate: Date | null = null;

  @Widget.Reactive()
  protected selectedDateStr: string = '';

  // ========== Off-duty staff ==========
  @Widget.Reactive()
  protected selectedEmployees: EmployeeItem[] = [];

  @Widget.Reactive()
  protected showStaffDropdown: boolean = false;

  @Widget.Reactive()
  protected availableStaff: EmployeeItem[] = [];

  // All employees loaded from server (for client-side pagination & search)
  @Widget.Reactive()
  protected allEmployees: EmployeeItem[] = [];

  @Widget.Reactive()
  protected staffSearchKeyword: string = '';

  @Widget.Reactive()
  protected employeeCurrentPage: number = 1;

  @Widget.Reactive()
  protected employeePageSize: number = 20;

  @Widget.Reactive()
  protected offRecords: OffRecord[] = [];

  // Map to track which days have off records (for calendar highlighting)
  @Widget.Reactive()
  protected monthOffDaysMap: Record<string, boolean> = {};

  private dutyTimeId: number | null = null;

  public initialize(props: any) {
    super.initialize(props);
    this.setComponent(DutyScheduleCalendarVue);
    return this;
  }

  public async mounted() {
    await this.loadDutyTimeConfig();
    await this.loadAvailableStaff();
  }

  // ==================== API Calls ====================

  private async loadDutyTimeConfig() {
    try {
      const gql = `
        query {
          deliAriesSellerDutyScheduleQuery {
            queryByEntity(query: {}) {
              id
              startTime
              endTime
              changeLogs {
                id
                startTime
                endTime
                updateByName
                updateTime
              }
            }
          }
        }
      `;
      const res = await http.query<any>(MODULE_NAME, gql);
      const result = res?.data?.deliAriesSellerDutyScheduleQuery?.queryByEntity;
      if (result) {
        this.dutyTimeId = result.id;
        this.dutyStartTime = result.startTime || '';
        this.dutyEndTime = result.endTime || '';
        this.changeLogs = result.changeLogs || [];
      }
    } catch (e) {
      console.error('Failed to load duty time config', e);
    }
  }

  private async loadAvailableStaff() {
    try {
      const gql = `
        query {
          deliAriesSellerDutyScheduleQuery {
            queryAvailableCustomerService {
              availableEmployees {
                id
                name
                jobNumber
              }
            }
          }
        }
      `;
      const res = await http.query<any>(MODULE_NAME, gql);
      const result = res?.data?.deliAriesSellerDutyScheduleQuery?.queryAvailableCustomerService;
      const employees = result?.availableEmployees;
      if (employees) {
        const mapped = employees.map((e: any) => ({
          id: e.id,
          name: e.name || '',
          code: e.jobNumber || ''
        }));
        this.allEmployees = mapped;
        this.employeeCurrentPage = 1;
        this.refreshPagedStaff(mapped);
      }
    } catch (e) {
      console.error('Failed to load available staff', e);
    }
  }

  private refreshPagedStaff(sourceList?: EmployeeItem[]) {
    const all = sourceList ?? this.allEmployees;
    const kw = this.staffSearchKeyword.trim().toLowerCase();
    const list = kw
      ? all.filter((e) => e.name.toLowerCase().includes(kw) || e.code.toLowerCase().includes(kw))
      : all;
    this.filteredTotal = list.length;
    this.employeeTotalPages = Math.ceil(list.length / this.employeePageSize) || 1;
    const start = (this.employeeCurrentPage - 1) * this.employeePageSize;
    this.availableStaff = list.slice(start, start + this.employeePageSize);
  }

  @Widget.Reactive()
  protected employeeTotalPages: number = 1;

  @Widget.Reactive()
  protected filteredTotal: number = 0;

  @Widget.Method()
  protected async onEmployeePageChange(page: number) {
    if (page >= 1 && page <= this.employeeTotalPages) {
      this.employeeCurrentPage = page;
      this.refreshPagedStaff();
    }
  }

  private async loadOffRecordsByDate(date: Date) {
    try {
      const dateStr = this.toDateString(date);
      const gql = `
        query {
          deliAriesSellerDutyScheduleQuery {
            queryDutyOffRecordsByDate(data: { selectedDate: "${dateStr}" }) {
              offRecords {
                id
                employeeId
                employeeName
                employeeCode
                startTime
                endTime
                updateByName
                updateTime
              }
              selectedEmployeeIds
            }
          }
        }
      `;
      const res = await http.query<any>(MODULE_NAME, gql);
      const result = res?.data?.deliAriesSellerDutyScheduleQuery?.queryDutyOffRecordsByDate;
      if (result) {
        this.offRecords = result.offRecords || [];
        // 切换日期时：若位置2（offRecords）有记录，则从offRecords反推填充位置1（不值班客服）
        // 优先使用 offRecords 派生，避免后端 selectedEmployeeIds 缺失导致位置1为空
        if (this.offRecords.length > 0) {
          const idSet = new Set<number>();
          const codeSet = new Set<string>();
          this.offRecords.forEach((r) => {
            if (r.employeeId != null) {
              idSet.add(Number(r.employeeId));
            } else if (r.employeeCode) {
              codeSet.add(r.employeeCode);
            }
          });
          this.selectedEmployees = this.allEmployees.filter(
            (s) => idSet.has(Number(s.id)) || codeSet.has(s.code)
          );
        } else {
          const selectedIds: number[] = result.selectedEmployeeIds || [];
          this.selectedEmployees = this.allEmployees.filter((s) => selectedIds.includes(Number(s.id)));
        }
        // Mark day as having off records
        const key = this.toDateKey(date);
        this.monthOffDaysMap = { ...this.monthOffDaysMap, [key]: this.offRecords.length > 0 };
      }
    } catch (e) {
      console.error('Failed to load off records', e);
    }
  }

  private async apiSaveDutyTime(startTime: string, endTime: string) {
    const gql = `
      mutation {
        deliAriesSellerDutyScheduleMutation {
          saveDutyTime(data: { startTime: "${startTime}", endTime: "${endTime}" }) {
            id
            startTime
            endTime
          }
        }
      }
    `;
    const res = await http.query<any>(MODULE_NAME, gql);
    return res?.data?.deliAriesSellerDutyScheduleMutation?.saveDutyTime;
  }

  private async apiSaveDutyOffRecords(dateStr: string, employeeIds: number[]) {
    const idsStr = employeeIds.join(', ');
    const gql = `
      mutation {
        deliAriesSellerDutyScheduleMutation {
          saveDutyOffRecords(data: { selectedDate: "${dateStr}", selectedEmployeeIds: [${idsStr}] }) {
            offRecords {
              id
              employeeName
              employeeCode
              startTime
              endTime
              updateByName
              updateTime
            }
          }
        }
      }
    `;
    const res = await http.query<any>(MODULE_NAME, gql);
    return res?.data?.deliAriesSellerDutyScheduleMutation?.saveDutyOffRecords;
  }

  // ==================== Year Picker ====================

  @Widget.Reactive()
  protected get yearPickerList(): number[] {
    const list: number[] = [];
    for (let i = 0; i < 12; i++) {
      list.push(this.yearPickerStart + i);
    }
    return list;
  }

  @Widget.Method()
  protected toggleYearPicker() {
    this.showYearPicker = !this.showYearPicker;
  }

  @Widget.Method()
  protected changeYearPickerRange(delta: number) {
    this.yearPickerStart += delta;
  }

  @Widget.Method()
  protected changeYear(delta: number) {
    this.currentYear += delta;
    this.expandedMonth = 0;
  }

  @Widget.Method()
  protected selectYear(year: number) {
    this.currentYear = year;
    this.showYearPicker = false;
    this.expandedMonth = 0;
  }

  // ==================== Month/Day Calendar ====================

  @Widget.Method()
  protected toggleMonth(month: number) {
    this.expandedMonth = this.expandedMonth === month ? 0 : month;
  }

  @Widget.Method()
  protected getMonthDays(year: number, month: number): number[] {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    let startWeekDay = firstDay.getDay(); // 0=Sun, 1=Mon...
    startWeekDay = startWeekDay === 0 ? 6 : startWeekDay - 1; // Mon=0

    const days: number[] = [];
    for (let i = 0; i < startWeekDay; i++) {
      days.push(0);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }
    return days;
  }

  @Widget.Method()
  protected isToday(year: number, month: number, day: number): boolean {
    if (day <= 0) return false;
    const now = new Date();
    return now.getFullYear() === year && now.getMonth() + 1 === month && now.getDate() === day;
  }

  @Widget.Method()
  protected isSelectedDate(year: number, month: number, day: number): boolean {
    if (day <= 0 || !this.selectedDate) return false;
    return (
      this.selectedDate.getFullYear() === year &&
      this.selectedDate.getMonth() + 1 === month &&
      this.selectedDate.getDate() === day
    );
  }

  @Widget.Method()
  protected hasOffRecords(year: number, month: number, day: number): boolean {
    if (day <= 0) return false;
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return !!this.monthOffDaysMap[key];
  }

  // ==================== Day Click ====================

  @Widget.Method()
  protected async onDayClick(year: number, month: number, day: number) {
    const date = new Date(year, month - 1, day);
    this.selectedDate = date;
    this.selectedDateStr = `${year}年${month}月${day}日`;
    this.showStaffDropdown = false;
    await this.loadOffRecordsByDate(date);
  }

  // ==================== Save Actions ====================

  @Widget.Method()
  protected toggleChangeLog() {
    this.showChangeLog = !this.showChangeLog;
  }

  @Widget.Method()
  protected onDutyStartTimeChange(value: string) {
    this.dutyStartTime = value;
  }

  @Widget.Method()
  protected onDutyEndTimeChange(value: string) {
    this.dutyEndTime = value;
  }

  @Widget.Method()
  protected async onSaveDutyTime() {
    if (!this.dutyStartTime) {
      MessageHub.error('请选择值班开始时间');
      return;
    }
    if (!this.dutyEndTime) {
      MessageHub.error('请选择值班结束时间');
      return;
    }
    try {
      const result = await this.apiSaveDutyTime(this.dutyStartTime, this.dutyEndTime);
      if (result) {
        this.dutyTimeId = result.id;
        MessageHub.info('值班时间保存成功');
        await this.loadDutyTimeConfig();
      }
    } catch (e) {
      console.error('Failed to save duty time', e);
      MessageHub.error('保存值班时间失败');
    }
  }

  @Widget.Method()
  protected async onSaveOffDuty() {
    if (!this.selectedDate) {
      MessageHub.error('请先选择日期');
      return;
    }
    try {
      const dateStr = this.toDateString(this.selectedDate);
      const selectedIds = this.selectedEmployees.map((e) => e.id);
      const result = await this.apiSaveDutyOffRecords(dateStr, selectedIds);
      if (result) {
        this.offRecords = result.offRecords || [];
        MessageHub.info('不值班客服保存成功');
        const key = this.toDateKey(this.selectedDate);
        this.monthOffDaysMap = { ...this.monthOffDaysMap, [key]: this.offRecords.length > 0 };
      }
    } catch (e) {
      console.error('Failed to save off duty records', e);
      MessageHub.error('保存不值班客服失败');
    }
  }

  // ==================== Employee Selection ====================

  @Widget.Method()
  protected toggleStaffDropdown() {
    this.showStaffDropdown = !this.showStaffDropdown;
    if (this.showStaffDropdown) {
      this.staffSearchKeyword = '';
      this.employeeCurrentPage = 1;
      this.refreshPagedStaff();
    }
  }

  @Widget.Method()
  protected onStaffSearch(keyword: string) {
    this.staffSearchKeyword = keyword;
    this.employeeCurrentPage = 1;
    this.refreshPagedStaff();
  }

  @Widget.Method()
  protected removeEmployee(employeeId: number) {
    this.selectedEmployees = this.selectedEmployees.filter((e) => e.id !== employeeId);
  }

  @Widget.Method()
  protected isStaffSelected(staffId: number): boolean {
    return this.selectedEmployees.some((e) => e.id === staffId);
  }

  @Widget.Method()
  protected toggleStaff(staff: EmployeeItem) {
    if (this.isStaffSelected(staff.id)) {
      this.selectedEmployees = this.selectedEmployees.filter((e) => e.id !== staff.id);
    } else {
      this.selectedEmployees = [...this.selectedEmployees, staff];
    }
  }

  // ==================== Utilities ====================

  @Widget.Method()
  protected formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}年${month}月${day}日${hours}:${minutes}:${seconds}`;
    } catch {
      return dateStr;
    }
  }

  private toDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d} 00:00:00`;
  }

  private toDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
